"use client";

import { useState } from "react";

import { Card, CardContent } from "@unreserved/ui/components/card";

import { CustomSwitch } from "@/app/_components/shared/switch";
import {
  AGENT_SAVINGS_INPUTS_CONFIG,
  AGENT_SAVINGS_SECTION,
  DEFAULT_AGENT_COMMISSION,
  DEFAULT_AGENT_MARKETING,
  DEFAULT_ESTIMATED_SALE_PRICE,
} from "@/app/_libs/data/landing-page/agent-savings-section";
import { calculateAgentSavings } from "@/app/_libs/utils/agent-savings";

import { Slider } from "./slider";

export function AgentSavingsSection() {
  const [mode, setMode] = useState<"diy" | "endToEnd">("diy");
  const [estimatedSalePrice, setEstimatedSalePrice] = useState<number>(
    DEFAULT_ESTIMATED_SALE_PRICE,
  );
  const [agentCommission, setAgentCommission] = useState<number>(DEFAULT_AGENT_COMMISSION);
  const [agentMarketing, setAgentMarketing] = useState<number>(DEFAULT_AGENT_MARKETING);

  const inputStateById = {
    "estimated-sale-price": {
      value: estimatedSalePrice,
      onChange: setEstimatedSalePrice,
    },
    "agent-commission": {
      value: agentCommission,
      onChange: setAgentCommission,
    },
    "agent-marketing": {
      value: agentMarketing,
      onChange: setAgentMarketing,
    },
  } as const;

  const { metricCards } = calculateAgentSavings({
    mode,
    estimatedSalePrice,
    agentCommission,
    agentMarketing,
  });

  const formatValue = (value: number, options: { prefix?: string; suffix?: string }): string => {
    const { prefix = "", suffix = "" } = options;
    // Format large numbers with commas
    const formattedValue = value.toLocaleString("en-AU");
    return `${prefix}${formattedValue}${suffix}`;
  };

  return (
    <section className="flex w-full scroll-mt-20 justify-center bg-white px-4 py-12 sm:px-6 md:px-[6%] md:py-20 lg:px-[7%] lg:py-[4%]">
      <div className="bg-background-50 flex w-[90vw] flex-col gap-12 rounded-2xl px-5 py-6 sm:px-7 sm:py-7 md:px-10 md:py-8 lg:w-full lg:max-w-[100vw] lg:flex-row lg:gap-10 lg:px-12 lg:py-16">
        <div className="flex w-full flex-1 flex-col gap-6 lg:max-w-[30vw] lg:gap-12">
          <div className="flex flex-col gap-3">
            <h2 className="text-base font-semibold text-black md:text-xl lg:text-2xl">
              {AGENT_SAVINGS_SECTION.heading}
            </h2>
            <p className="text-typography-800 text-xs leading-normal md:text-sm lg:text-base">
              {AGENT_SAVINGS_SECTION.description}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:grid-cols-2 xl:grid-cols-3">
            {metricCards.map((card) => (
              <Card
                key={card.id}
                className={`flex items-start gap-3 rounded-md px-3 py-2 ${
                  card.id === "agent-cost"
                    ? "text-typography-0 bg-[#5F6774]"
                    : card.id === "unreserved"
                      ? "text-typography-0 bg-black"
                      : "bg-primary-700 text-white"
                }`}>
                <CardContent className="flex flex-col gap-1 p-0">
                  <p className="text-typography-0 text-base font-semibold lg:text-lg">
                    {formatValue(card.amount, { prefix: "$ " })}
                  </p>
                  <p className="text-typography-0 text-xs font-normal lg:text-sm">{card.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="flex w-full flex-1 flex-col gap-6 lg:ml-auto lg:max-w-[40vw]">
          <div className="flex items-center justify-between gap-4">
            <p className="text-typography-700 text-lg font-bold tracking-[0.2px] lg:text-2xl">
              Inputs
            </p>
            <div className="text-typography-700 flex items-center gap-3">
              <p
                className="text-xs font-medium md:text-sm lg:text-lg"
                onClick={() => setMode("diy")}>
                DIY
              </p>
              <CustomSwitch
                checked={mode === "endToEnd"}
                onCheckedChange={(checked) => setMode(checked ? "endToEnd" : "diy")}
                aria-label="Toggle between DIY and End to End Service"
                // className="scale-[0.7] lg:scale-[0.85] lg:-translate-y-[1px]" // previous styling (riya dhingra)
                className="data-[state=checked]:bg-background-800 dark:data-[state=unchecked]:bg-background-800 h-3.5 w-10 cursor-pointer"
                thumbClassName="data-[state=checked]:translate-x-full data-[state=unchecked]:translate-x-[-2px] shadow-switch w-5 h-5 dark:data-[state=checked]:bg-white"
              />

              <p
                className="text-xs font-medium whitespace-nowrap md:text-sm lg:text-lg"
                onClick={() => setMode("endToEnd")}>
                End to End Service
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-5">
            {AGENT_SAVINGS_INPUTS_CONFIG.map(
              ({ id, label, min, max, valuePrefix, valueSuffix, step = 1 }) => {
                const { value, onChange } =
                  inputStateById[id as keyof typeof inputStateById] ??
                  inputStateById["estimated-sale-price"];

                const labelId = `slider-label-${id}`;

                return (
                  <div
                    key={id}
                    className="flex w-full flex-col"
                    role="group"
                    aria-labelledby={labelId}>
                    <div className="flex items-center justify-between text-black">
                      <span
                        id={labelId}
                        className="w-[120px] text-sm font-medium whitespace-pre-wrap lg:w-[200px] lg:text-lg">
                        {label}
                      </span>
                      <p className="text-right text-sm font-semibold md:text-sm lg:text-lg">
                        {formatValue(value, {
                          prefix: valuePrefix,
                          suffix: valueSuffix,
                        })}
                      </p>
                    </div>
                    <Slider
                      min={min}
                      max={max}
                      step={step}
                      value={[value]}
                      onValueChange={([next]: number[]) => onChange(next)}
                      aria-label={label}
                      aria-labelledby={labelId}
                      className="**:data-[slot=slider-track]:bg-background-300 h-7 **:data-[slot=slider-range]:bg-black **:data-[slot=slider-thumb]:border-transparent **:data-[slot=slider-thumb]:bg-black **:data-[slot=slider-thumb]:[box-shadow:-2px_2px_8px_rgba(38,38,38,0.2)] **:data-[slot=slider-track]:h-[5px]! **:data-[slot=slider-track]:rounded-full sm:h-8 md:h-12"
                    />
                  </div>
                );
              },
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
